import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { dispatch } from '../store';
import { updateError } from './user';

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState = {
    calendly: {
        config: null,       // null = not connected, object = connected
        loading: false,
        error: null,
    },
    calcom: {
        config: null,
        loading: false,
        error: null,
    },
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const slice = createSlice({
    name: 'bookingIntegration',
    initialState,
    reducers: {
        setCalendlyLoading(state, action) {
            state.calendly.loading = action.payload;
            state.calendly.error = null;
        },
        setCalendlyConfig(state, action) {
            state.calendly.config = action.payload;
            state.calendly.loading = false;
            state.calendly.error = null;
        },
        setCalendlyError(state, action) {
            state.calendly.error = action.payload;
            state.calendly.loading = false;
        },
        setCalcomLoading(state, action) {
            state.calcom.loading = action.payload;
            state.calcom.error = null;
        },
        setCalcomConfig(state, action) {
            state.calcom.config = action.payload;
            state.calcom.loading = false;
            state.calcom.error = null;
        },
        setCalcomError(state, action) {
            state.calcom.error = action.payload;
            state.calcom.loading = false;
        },
    },
});

export default slice.reducer;

// ─── Calendly thunks ──────────────────────────────────────────────────────────

export function getCalendlyConfig(practiceId) {
    return async () => {
        if (!hasFeatureAccess('calendly-integration')) return;
        dispatch(slice.actions.setCalendlyLoading(true));
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/calendly-config/`);
            if (response.status === 200) {
                dispatch(slice.actions.setCalendlyConfig(response.data));
            } else if (response.status === 404) {
                dispatch(slice.actions.setCalendlyConfig(null));
            } else {
                dispatch(slice.actions.setCalendlyError(response.data?.detail || 'Failed to load Calendly config'));
            }
        } catch (err) {
            dispatch(slice.actions.setCalendlyError(err.message || 'Something went wrong'));
        }
    };
}

export function connectCalendly(practiceId, token, onSuccess, onError) {
    return async () => {
        if (!hasFeatureAccess('calendly-integration')) return;
        dispatch(slice.actions.setCalendlyLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/calendly-config/`,
                'POST',
                { personal_access_token: token }
            );
            if (response.status === 201 || response.status === 200) {
                dispatch(slice.actions.setCalendlyConfig(response.data));
                if (onSuccess) onSuccess();
            } else {
                const msg = response.data?.error || 'Failed to connect Calendly';
                dispatch(slice.actions.setCalendlyError(msg));
                if (onError) onError(msg);
            }
        } catch (err) {
            const msg = err.message || 'Something went wrong';
            dispatch(slice.actions.setCalendlyError(msg));
            if (onError) onError(msg);
        }
    };
}

export function updateCalendlyEventTypes(practiceId, eventTypes, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('calendly-integration')) return;
        dispatch(slice.actions.setCalendlyLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/calendly-config/event-types/`,
                'PUT',
                eventTypes
            );
            if (response.status === 200) {
                dispatch(slice.actions.setCalendlyConfig(response.data));
                if (onSuccess) onSuccess();
            } else {
                dispatch(slice.actions.setCalendlyError(response.data?.detail || 'Failed to update event types'));
            }
        } catch (err) {
            dispatch(slice.actions.setCalendlyError(err.message || 'Something went wrong'));
        }
    };
}

export function disconnectCalendly(practiceId, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('calendly-integration')) return;
        dispatch(slice.actions.setCalendlyLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/calendly-config/`,
                'DELETE'
            );
            if (response.status === 204) {
                dispatch(slice.actions.setCalendlyConfig(null));
                dispatch(updateError({ success: true, message: 'Calendly disconnected' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(slice.actions.setCalendlyError('Failed to disconnect Calendly'));
            }
        } catch (err) {
            dispatch(slice.actions.setCalendlyError(err.message || 'Something went wrong'));
        }
    };
}

// ─── Cal.com thunks ───────────────────────────────────────────────────────────

export function getCalcomConfig(practiceId) {
    return async () => {
        if (!hasFeatureAccess('calcom-integration')) return;
        dispatch(slice.actions.setCalcomLoading(true));
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/calcom-config/`);
            if (response.status === 200) {
                dispatch(slice.actions.setCalcomConfig(response.data));
            } else if (response.status === 404) {
                dispatch(slice.actions.setCalcomConfig(null));
            } else {
                dispatch(slice.actions.setCalcomError(response.data?.detail || 'Failed to load Cal.com config'));
            }
        } catch (err) {
            dispatch(slice.actions.setCalcomError(err.message || 'Something went wrong'));
        }
    };
}

export function saveCalcomConfig(practiceId, data, onSuccess, onError) {
    return async () => {
        if (!hasFeatureAccess('calcom-integration')) return;
        dispatch(slice.actions.setCalcomLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/calcom-config/`,
                'POST',
                data
            );
            if (response.status === 201 || response.status === 200) {
                dispatch(slice.actions.setCalcomConfig(response.data));
                if (onSuccess) onSuccess();
            } else {
                const msg = response.data?.error || response.data?.non_field_errors?.[0] || 'Failed to save Cal.com config';
                dispatch(slice.actions.setCalcomError(msg));
                if (onError) onError(msg);
            }
        } catch (err) {
            const msg = err.message || 'Something went wrong';
            dispatch(slice.actions.setCalcomError(msg));
            if (onError) onError(msg);
        }
    };
}

export function disconnectCalcom(practiceId, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('calcom-integration')) return;
        dispatch(slice.actions.setCalcomLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/calcom-config/`,
                'DELETE'
            );
            if (response.status === 204) {
                dispatch(slice.actions.setCalcomConfig(null));
                dispatch(updateError({ success: true, message: 'Cal.com disconnected' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(slice.actions.setCalcomError('Failed to disconnect Cal.com'));
            }
        } catch (err) {
            dispatch(slice.actions.setCalcomError(err.message || 'Something went wrong'));
        }
    };
}
